/**
 * Overview — the gateway at a glance.
 *
 * Everything here comes from endpoints the router actually serves:
 *   /api/v1/platform/model-gateway/status   catalog size, keys, guardrails, rollout
 *   /api/v1/platform/source-providers       upstream providers
 *   /api/v1/platform/monitoring             rolling 60s traffic window
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, AlertCircle, CheckCircle2, Code2, KeyRound, Loader2,
  Network, RefreshCw, Shield, XCircle,
} from 'lucide-react';
import { api } from '../lib/api.js';
import { Badge, GlassButton, GlassCard, GradientText, PageHeader, StatCard } from '../ui/index.js';

interface GatewayStatus {
  enabled: boolean;
  upstream: string;
  primaryKeyConfigured: boolean;
  backupKeyConfigured: boolean;
  catalogCount: number;
  exposedCount: number;
  guardrailMode: string;
  guardrailsEnabled: boolean;
  financePreflightEnabled: boolean;
  rolloutMode: string;
}

interface SourceProvider {
  id: string;
  displayName: string;
  enabled: boolean;
  apiFormat: string;
}

interface MonitoringSnapshot {
  requestsPerSec: number;
  errorRate: number;
  costPerSec: number;
  latencyP50Ms: number;
  latencyP95Ms: number;
  totalRequests: number;
}

function formatUsd(value: number): string {
  return `$${value.toFixed(value < 1 ? 4 : 2)}`;
}

export function OperatorDashboardPage() {
  const [status, setStatus] = useState<GatewayStatus | null>(null);
  const [providers, setProviders] = useState<SourceProvider[]>([]);
  const [monitoring, setMonitoring] = useState<MonitoringSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      setError(null);
      const [statusResult, providerResult, monitoringResult] = await Promise.all([
        api.get<GatewayStatus>('/api/v1/platform/model-gateway/status'),
        api.get<{ providers: SourceProvider[] }>('/api/v1/platform/source-providers').catch(() => null),
        api.get<MonitoringSnapshot>('/api/v1/platform/monitoring').catch(() => null),
      ]);
      setStatus(statusResult);
      setProviders(providerResult?.providers ?? []);
      setMonitoring(monitoringResult);
    } catch {
      setError('Could not load the gateway status.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 15_000);
    return () => clearInterval(timer);
  }, []);

  if (loading && !status) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  const enabledProviders = providers.filter((p) => p.enabled);

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={<GradientText>Gateway Overview</GradientText>}
        subtitle="The OpenAI-compatible AI gateway at router.rule42.ai."
        action={
          <GlassButton variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => void load()}>
            Refresh
          </GlassButton>
        }
      />

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-400/30 bg-rose-400/10 px-3 py-3 text-sm text-rose-200">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {status && !status.enabled && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-3 text-sm text-amber-200">
          <AlertCircle className="h-4 w-4" />
          <span>
            The gateway is switched off — <code>/v1</code> requests are being refused. Enable it in{' '}
            <Link className="underline" to="/operator/settings/model-gateway">Model Gateway</Link>.
          </span>
        </div>
      )}

      {/* Traffic — rolling 60 second window */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Requests / sec"
          value={monitoring ? monitoring.requestsPerSec.toFixed(2) : '—'}
          change="last 60s"
          changeType="neutral"
          icon={<Activity className="h-4 w-4" />}
        />
        <StatCard
          label="Latency p95"
          value={monitoring ? `${Math.round(monitoring.latencyP95Ms)} ms` : '—'}
          change={monitoring ? `p50 ${Math.round(monitoring.latencyP50Ms)} ms` : undefined}
          changeType="neutral"
          icon={<Activity className="h-4 w-4" />}
        />
        <StatCard
          label="Error rate"
          value={monitoring ? `${(monitoring.errorRate * 100).toFixed(1)}%` : '—'}
          change={monitoring ? `${monitoring.totalRequests} request(s)` : undefined}
          changeType={monitoring && monitoring.errorRate > 0.05 ? 'negative' : 'neutral'}
          icon={<AlertCircle className="h-4 w-4" />}
        />
        <StatCard
          label="Cost / sec"
          value={monitoring ? formatUsd(monitoring.costPerSec) : '—'}
          change="billed to callers"
          changeType="neutral"
          icon={<Activity className="h-4 w-4" />}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Catalog + upstreams */}
        <GlassCard className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
              <Network className="h-4 w-4 text-indigo-400" /> Catalog
            </h3>
            <Link className="text-xs text-indigo-300 hover:text-indigo-200" to="/operator/settings/model-gateway">
              Manage →
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Metric label="Models" value={status ? String(status.catalogCount) : '—'} />
            <Metric label="Exposed" value={status ? String(status.exposedCount) : '—'} />
          </div>

          {status?.catalogCount === 0 && (
            <p className="text-xs text-amber-300/80">
              The catalog is empty, so <code>/v1/models</code> returns nothing. Add models from a source provider.
            </p>
          )}

          <div className="space-y-2 border-t border-white/5 pt-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/35">
              Upstream providers ({enabledProviders.length} enabled)
            </p>
            {providers.length === 0 ? (
              <p className="text-xs text-white/40">No source providers configured.</p>
            ) : (
              providers.map((provider) => (
                <div key={provider.id} className="flex items-center justify-between text-sm">
                  <span className="text-white/75">{provider.displayName}</span>
                  <Badge variant={provider.enabled ? 'success' : 'default'}>
                    {provider.enabled ? 'enabled' : 'disabled'}
                  </Badge>
                </div>
              ))
            )}
          </div>
        </GlassCard>

        {/* Posture */}
        <GlassCard className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
              <Shield className="h-4 w-4 text-indigo-400" /> Posture
            </h3>
            <Link className="text-xs text-indigo-300 hover:text-indigo-200" to="/operator/settings/preflight-checks">
              Configure →
            </Link>
          </div>

          <Check label="Gateway enabled" ok={Boolean(status?.enabled)} />
          <Check label="Upstream key configured" ok={Boolean(status?.primaryKeyConfigured)} />
          <Check
            label="Finance pre-flight"
            ok={Boolean(status?.financePreflightEnabled)}
            offLabel="off — calls are not credit-checked"
          />
          <Check
            label="Guardrails"
            ok={Boolean(status?.guardrailsEnabled)}
            offLabel="off"
            okLabel={status?.guardrailMode}
          />

          <div className="flex items-center justify-between border-t border-white/5 pt-3 text-sm">
            <span className="text-white/50">Rollout</span>
            <span className="text-white/80">{status?.rolloutMode ?? '—'}</span>
          </div>
        </GlassCard>
      </div>

      {/* Where to go next */}
      <div className="grid gap-4 sm:grid-cols-2">
        <QuickLink
          to="/operator/api-keys"
          icon={<KeyRound className="h-4 w-4" />}
          title="API Keys"
          body="Issue keys so services and third parties can call the gateway without a browser login."
        />
        <QuickLink
          to="/operator/developer-guide"
          icon={<Code2 className="h-4 w-4" />}
          title="Developer Guide"
          body="Base URL, authentication, streaming, BYOK and error handling — the docs to hand to a caller."
        />
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-white/35">{label}</p>
      <p className="text-2xl text-white/90">{value}</p>
    </div>
  );
}

function Check({ label, ok, okLabel, offLabel }: { label: string; ok: boolean; okLabel?: string; offLabel?: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-white/70">{label}</span>
      <span className={`flex items-center gap-1.5 ${ok ? 'text-emerald-300' : 'text-white/40'}`}>
        {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
        {ok ? (okLabel ?? 'on') : (offLabel ?? 'off')}
      </span>
    </div>
  );
}

function QuickLink({ to, icon, title, body }: { to: string; icon: React.ReactNode; title: string; body: string }) {
  return (
    <Link to={to}>
      <GlassCard className="h-full space-y-2 transition-colors hover:border-indigo-400/40">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
          <span className="text-indigo-400">{icon}</span>
          {title}
        </h3>
        <p className="text-xs leading-relaxed text-white/50">{body}</p>
      </GlassCard>
    </Link>
  );
}
